import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Select,
    Box,
    Stack,
    Divider,
    IconButton,
    Radio,
    RadioGroup,
    FormControl,
    FormLabel,
    InputLabel,
} from '@mui/material';
import { SignOnBehalfIcon } from 'src/pages/schedule/therapist/UI-Improvment/schedule-icon';
import { useSelector } from 'react-redux';
import PinInput from 'src/components/signature/pin-input';
import SetupSigningWizard from 'src/components/signature/setup-signing-wizard';
import SigningLockedDialog from 'src/components/signature/signing-locked-dialog';
import { useSignatureValidation } from 'src/hooks/use-signature-validation';
import SignatureOnBehalfSkeleton from './signature-on-behalf-skeleton';

interface TherapistInfo {
    name: string;
    credentials: string;
    license: string;
    role: string;
}

interface SignOnBehalfModalProps {
    onClose: () => void;
    //  consentConfirmation is the `agree` checkbox — recorded per signature, so it travels with the
    //  submission rather than being assumed by the caller. `pin` is present only when signature
    //  validation (THIN-7681) is on — BRK-2059 closes the THIN-7775 proxy-PIN gap, so on-behalf
    //  signs are PIN-verified exactly like direct signs.
    onSign: (document: any, signature: string, reason: string, consentConfirmation: boolean, pin?: string) => void;
    signOnBehalfDoc: any;
    //  BRK-2059: axis-aware labels. The legacy DOR flow keeps the defaults; the compliance flow
    //  passes 'Supervisor' (supervisor-axis cosign) and 'Signer' (the acting user may hold any role).
    onBehalfOfLabel?: string;
    signerLabel?: string;
}

const REASONS = [
    'PTO / Vacation Coverage',
    'System Access Issues',
    'Illness / Out Sick',
    'Shift Coverage / PRN Support',
    'Clinician No Longer at Facility',
    'Documentation Assistance – Per Handoff',
    'EHR Login Delay / Pending Credentials',
    'Weekend or Holiday Coverage',
    'Emergency Absence / Unplanned Leave',
    'Technical Error / System Crash',
];

const SignOnBehalfModal: React.FC<SignOnBehalfModalProps> = ({
    onClose,
    onSign,
    signOnBehalfDoc,
    onBehalfOfLabel = 'Therapist',
    signerLabel = 'DOR',
}) => {
    const therapistRoles = useSelector(
        (state: any) => state?.getTherapistProfileReducer?.response?.data
    );
    const getProviderInfoOfSignOnBehalf = useSelector((state:any)=>state.getTherapistInfoReducer)
    const signOnBehalf=useSelector((state:any)=>state.addSignOnBehalfReducer)
    const [signature, setSignature] = useState('');
    const [reason, setReason] = useState('');
    const [agree, setAgree] = useState(false);
    const [confirm, setConfirm] = useState(false);

    //  THIN-7681/BRK-2059: when the tenant flag is on, the signer's name is HR-rendered
    //  (view-only) and the signing PIN replaces the free-typed signature — same contract as the
    //  direct-sign modal (signature-document-popup). Flag off ⇒ the legacy typed-signature
    //  rendering below is untouched.
    const { enabled: signatureValidationEnabled, hasPin, statusLoading } = useSignatureValidation();
    const showSetup = signatureValidationEnabled && !hasPin && !statusLoading;
    const signerFullName = [therapistRoles?.lastName, therapistRoles?.firstName]
        .filter(Boolean)
        .join(' ');
    const [pin, setPin] = useState('');
    const [pinError, setPinError] = useState<string | null>(null);
    const [locked, setLocked] = useState<{ open: boolean; until: string | null }>({
        open: false,
        until: null,
    });

    //  THIN-7706 structured PIN-challenge outcomes, surfaced inline rather than via the parent's
    //  generic close-and-toast (which must skip these — see signature-template-new).
    useEffect(() => {
        if (!signatureValidationEnabled) return;
        if (signOnBehalf?.response === null || signOnBehalf?.isSignOnBehalf) return;
        const outcome = signOnBehalf?.response?.data?.outcome;
        if (outcome === 'LOCKED') {
            setLocked({ open: true, until: signOnBehalf?.response?.data?.lockedUntil ?? null });
            setPin('');
            return;
        }
        if (outcome === 'WRONG_PIN') {
            const left = signOnBehalf?.response?.data?.attemptsRemaining;
            setPinError(
                `Incorrect PIN.${left != null ? ` ${left} attempt${left === 1 ? '' : 's'} remaining.` : ''}`
            );
            setPin('');
            return;
        }
        if (outcome === 'NO_PIN_SET') {
            setPinError('No signing PIN set yet. Set one up in Profile → Signature & PIN.');
        }
    }, [signOnBehalf, signatureValidationEnabled]);

    const handleSign = () => {
        if (signatureValidationEnabled) {
            if (pin.length !== 4 || !reason || !agree || !confirm) return;
            setPinError(null);
            //  view-only name; the backend HR-renders the signed name regardless (THIN-7695)
            onSign(signOnBehalfDoc, signerFullName, reason, agree, pin);
            return;
        }
        if (signature && reason && agree && confirm) {
            onSign(signOnBehalfDoc,signature, reason, agree);
        }
    };

    const isSignEnabled = signatureValidationEnabled
        ? pin.length === 4 && reason && agree && confirm
        : signature && reason && agree && confirm;

    return (
        <>{
            getProviderInfoOfSignOnBehalf?.loading ? <SignatureOnBehalfSkeleton /> : <>
                <DialogTitle>Signing on behalf of</DialogTitle>
                <DialogContent>
                    {showSetup ? (
                        //  First-ever PIN-verified sign: run the one-time setup wizard before the
                        //  challenge, exactly like the direct-sign modal.
                        <SetupSigningWizard onCancel={onClose} />
                    ) : (
                    <>
                    <Box mb={2}>
                        <Stack
                            direction="row"
                            spacing={4}
                        >
                            {/* On-behalf-of card (therapist, or the assigned supervisor) */}
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Box
                                    sx={{
                                        background: '#F5F7FF',
                                        borderRadius: '12px',
                                        width: 48,
                                        height: 48,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: 1,
                                    }}
                                >
                                    {/* Icon/avatar placeholder */}

                                    <SignOnBehalfIcon />
                                </Box>
                                <Box>
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                    >
                                        {onBehalfOfLabel}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                        <Typography variant="boldText">Name</Typography>
                                        <Typography
                                            variant="body3"
                                            noWrap
                                        >
                                            {`${getProviderInfoOfSignOnBehalf?.response?.data?.lastName} ${getProviderInfoOfSignOnBehalf?.response?.data?.firstName}`}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1,alignItems:'center' }}>
                                        <Typography variant="boldText">Credentials</Typography>
                                        <Typography
                                            variant="body3"
                                            noWrap
                                        >
                                            {getProviderInfoOfSignOnBehalf?.response?.data?.credentials}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1,alignItems:'center' }}>
                                        <Typography variant="boldText">License No.</Typography>
                                        <Typography
                                            variant="body3"
                                            noWrap
                                        >
                                          {getProviderInfoOfSignOnBehalf?.response?.data?.licenseNo}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                            <Divider
                                orientation="vertical"
                                flexItem
                            />
                            {/* Acting-signer card */}
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Box
                                    sx={{
                                        background: '#F5F7FF',
                                        borderRadius: '12px',
                                        width: 48,
                                        height: 48,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: 1,
                                    }}
                                >
                                    <SignOnBehalfIcon />
                                </Box>
                                <Box>
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                    >
                                        {signerLabel}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                        <Typography variant="boldText">Name</Typography>
                                        <Typography noWrap variant="body3">
                                            {`${therapistRoles?.lastName} ${therapistRoles?.firstName}`}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                        <Typography variant="boldText">Credentials</Typography>
                                        <Typography noWrap variant="body3">
                                            {therapistRoles?.credentials}{' '}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                        <Typography variant="boldText">License</Typography>
                                        <Typography noWrap variant="body3">
                                            {therapistRoles?.licenseNumber}
                                        </Typography>
                                    </Box>

                                </Box>
                            </Box>
                        </Stack>
                    </Box>
                    <Stack
                        flexDirection={'column'}
                        rowGap={1.5}
                    >
                        {signatureValidationEnabled ? (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    Enter your 4-digit signing PIN
                                </Typography>
                                <PinInput value={pin} onChange={setPin} error={!!pinError} mask autoFocus />
                                {pinError && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                        {pinError}
                                    </Typography>
                                )}
                            </Box>
                        ) : (
                        <TextField
                            fullWidth
                            label="Type your signature"
                            value={signature}
                            onChange={(e) => setSignature(e.target.value)}
                            sx={{ mb: 2 }}
                            InputProps={{
                                style: {
                                    fontFamily: 'cursive',
                                    fontSize: 24,
                                    color: '#6366F1',
                                },
                            }}
                        />
                        )}
                        <FormControl
                            fullWidth
                            sx={{ mb: 2 }}
                        >
                            <InputLabel id="reason-label">Reason for Proxy Signature</InputLabel>
                            <Select
                                labelId="reason-label"
                                value={reason}
                                label="Reason for Proxy Signature"
                                onChange={(e) => setReason(e.target.value)}
                                IconComponent={(props) => (
                                    <svg
                                        {...props}
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                    >
                                        <path
                                            d="M7 10l5 5 5-5"
                                            stroke="#6C737F"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                )}
                                renderValue={(selected) =>
                                    selected ? (
                                        selected
                                    ) : (
                                        <span style={{ color: '#6C737F' }}>Reason for Proxy Signature</span>
                                    )
                                }
                            >
                                {/* Add a disabled placeholder option for accessibility */}

                                {REASONS.map((r) => (
                                    <MenuItem
                                        key={r}
                                        value={r}
                                    >
                                        <Radio
                                            checked={reason === r}
                                            value={r}
                                            sx={{
                                                padding: 0,
                                                marginRight: 1.5,
                                            }}
                                        />
                                        {r}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={agree}
                                    onChange={(e) => setAgree(e.target.checked)}
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    I agree that my electronic signature is legally binding and holds the same validity
                                    as my handwritten signature, in accordance with applicable laws and regulations.
                                </Typography>
                            }
                            sx={{ alignItems: "flex-start" }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={confirm}
                                    onChange={(e) => setConfirm(e.target.checked)}
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    I confirm that I am signing on behalf of <b> {`${getProviderInfoOfSignOnBehalf?.response?.data?.lastName} ${getProviderInfoOfSignOnBehalf?.response?.data?.firstName}`}</b>  and take full responsibility for
                                    this action
                                </Typography>
                            }
                            sx={{ alignItems: "flex-start", mt: 1 }}

                        />
                    </Stack>
                    </>
                    )}
                </DialogContent>
                {!showSetup && (
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSign}
                        disabled={!isSignEnabled || signOnBehalf?.loading}
                    >
                        Sign
                    </Button>
                </DialogActions>
                )}
                <SigningLockedDialog
                    open={locked.open}
                    lockedUntil={locked.until}
                    onClose={() => setLocked({ open: false, until: null })}
                />
            </>
        }

        </>
    );
};

export default SignOnBehalfModal;
